/**
 * @file Analytics helpers for SAK engagement events.
 */

import appSettings from "../../settings/appSettings";
import { retrieveSakQuestionnaireData } from "../client-state/sakLocalStorage";
import { formatSakTopics } from "./analyticsUtils";
import { calculatePostPurchaseTaskCompletionTime } from "./observers";

const triggerKeys: string[] = [];

/**
 * Triggers a custom analytics event with the specified name and data.
 * @param eventName - The name of the analytics event.
 * @param eventData - The data associated with the event.
 */
export const triggerAnalyticsEvent = (
  eventName: string,
  eventData: Record<string, unknown> = {}
) => {
  // Log to console in development mode
  if (process.env.NODE_ENV === "development") {
    console.log("triggerAnalyticsEvent:", eventName, eventData);
  }

  const event = new CustomEvent(eventName, {
    detail: eventData
  });
  document.dispatchEvent(event);
};

/**
 * Triggers an analytics event based on the provided event trigger key.
 * @param eventTriggerKey - The key identifying the event to trigger.
 * @param data - Additional data to include with the event.
 */
export const triggerEvent = (eventTriggerKey: string, data: Record<string, unknown> = {}) => {
  const { sponsoredChildData } = appSettings;

  switch (eventTriggerKey) {
    case "sak_message_compose_view":
      triggerAnalyticsEvent(`sak_engagement_sak_message_compose_view`, {
        ...data,
        sponsoredChildData
      });
      break;
    case "sak_message_compose_start":
      triggerAnalyticsEvent(`sak_engagement_sak_message_compose_start`, {
        ...data,
        sponsoredChildData
      });
      break;
    case "sak_message_error":
      triggerAnalyticsEvent(`sak_engagement_sak_message_error`, {
        ...data,
        sponsoredChildData
      });
      break;
    default:
      console.warn(`Unhandled event trigger key: ${eventTriggerKey}`);
  }
};

/**
 * Triggers an analytics event once based on the provided event trigger key.
 * Ensures the event is only fired once per key.
 * @param eventTriggerKey - The key identifying the event to trigger.
 * @param data - Additional data to include with the event.
 */
export const triggerEventOnce = (eventTriggerKey: string, data: Record<string, unknown> = {}) => {
  if (triggerKeys.includes(eventTriggerKey)) {
    return;
  }
  triggerKeys.push(eventTriggerKey);

  const { sponsoredChildData } = appSettings;

  switch (eventTriggerKey) {
    case "sak_message_send_success": {
      const sakQestionnaireData = retrieveSakQuestionnaireData();
      const topics = formatSakTopics(sakQestionnaireData);
      triggerAnalyticsEvent(`sak_engagement_sak_message_send_success`, {
        ...data,
        topics,
        post_purchase_task_completion_time: calculatePostPurchaseTaskCompletionTime(),
        sponsoredChildData
      });
      break;
    }
    default:
      console.warn(`Unhandled event trigger key: ${eventTriggerKey}`);
  }
};
